"use client";

import * as React from "react";
import {
  Bot,
  CheckCircle2,
  Code2,
  CreditCard,
  GraduationCap,
  Package,
  TimerReset,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  faNum,
  faToman,
  faDateTime,
  faDate,
} from "@/lib/format";
import type {
  AdminStats,
  AIOrderStatus,
  PaymentStatus,
} from "./types";

type Props = { stats: AdminStats };

function StatusBadge({ status }: { status: PaymentStatus }) {
  if (status === "APPROVED")
    return (
      <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
        تأیید شده
      </Badge>
    );
  if (status === "REJECTED")
    return <Badge variant="destructive">رد شده</Badge>;
  return (
    <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400">
      در انتظار
    </Badge>
  );
}

function AiOrderStatusBadge({ status }: { status: AIOrderStatus }) {
  if (status === "DELIVERED")
    return (
      <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
        تحویل شده
      </Badge>
    );
  if (status === "REJECTED")
    return <Badge variant="destructive">رد شده</Badge>;
  return (
    <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400">
      در انتظار
    </Badge>
  );
}

function StatCard({
  label,
  value,
  icon,
  highlight,
  hint,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  highlight?: "amber" | "primary";
  hint?: string;
}) {
  return (
    <Card className="overflow-hidden rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs text-muted-foreground">
            {label}
          </CardTitle>
          <span
            className={`flex size-8 items-center justify-center rounded-lg ${
              highlight === "amber"
                ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                : "bg-primary/15 text-primary"
            }`}
          >
            {icon}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div
          className={`text-2xl font-bold ${
            highlight === "amber"
              ? "text-amber-600 dark:text-amber-400"
              : "text-foreground"
          }`}
        >
          {value}
        </div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

export default function AdminStats({ stats }: Props) {
  const {
    counts,
    revenueToman,
    recentPayments,
    recentSubs,
    recentAiOrders,
  } = stats;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="کاربران"
          value={faNum(counts.users)}
          icon={<Users className="size-4" />}
        />
        <StatCard
          label="پرداخت‌ها"
          value={faNum(counts.payments)}
          icon={<CreditCard className="size-4" />}
        />
        <StatCard
          label="اشتراک‌ها"
          value={faNum(counts.subs)}
          icon={<Package className="size-4" />}
        />
        <StatCard
          label="درآمد کل"
          value={faToman(revenueToman)}
          icon={<TrendingUp className="size-4" />}
          hint="مجموع پرداخت‌های تأیید شده"
        />
      </div>

      {/* Phase 2 stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="دوره‌ها"
          value={faNum(counts.courses)}
          icon={<GraduationCap className="size-4" />}
        />
        <StatCard
          label="سفارش‌های هوش مصنوعی"
          value={faNum(counts.aiOrders)}
          icon={<Bot className="size-4" />}
          highlight={counts.pendingAiOrders > 0 ? "amber" : undefined}
          hint={
            counts.pendingAiOrders > 0
              ? `${faNum(counts.pendingAiOrders)} مورد در انتظار`
              : undefined
          }
        />
        <StatCard
          label="درخواست‌های پروژه"
          value={faNum(counts.projectRequests)}
          icon={<Code2 className="size-4" />}
        />
        <StatCard
          label="درخواست تسویه"
          value={faNum(counts.pendingWithdrawals)}
          icon={<Wallet className="size-4" />}
          highlight={counts.pendingWithdrawals > 0 ? "amber" : undefined}
          hint={
            counts.pendingWithdrawals > 0
              ? "نیازمند بررسی"
              : "خیر"
          }
        />
      </div>

      <Card className="overflow-hidden rounded-2xl border-amber-500/30 bg-amber-500/5 shadow-sm">
        <CardContent className="flex flex-col items-start gap-1 py-4 sm:flex-row sm:items-center sm:justify-between sm:py-4">
          <div className="flex items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <TimerReset className="size-4" />
            </span>
            <div>
              <div className="text-sm text-muted-foreground">
                پرداخت‌های در انتظار تأیید
              </div>
              <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                {faNum(counts.pending)} مورد
              </div>
            </div>
          </div>
          {counts.pending > 0 && (
            <Badge className="bg-amber-500 text-white hover:bg-amber-500/90">
              نیاز به بررسی
            </Badge>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle className="text-base">آخرین پرداخت‌ها</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {recentPayments.length === 0 ? (
              <EmptyHint text="هنوز پرداختی ثبت نشده است." />
            ) : (
              <ul className="max-h-80 divide-y overflow-y-auto scrollbar-thin">
                {recentPayments.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-3 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span dir="ltr" className="text-sm font-medium">
                          {p.user?.phone ?? "—"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {p.plan?.name ?? "—"}
                        </span>
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {faDateTime(p.createdAt)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-primary">
                        {faToman(p.amountToman)}
                      </span>
                      <StatusBadge status={p.status} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle className="text-base">آخرین اشتراک‌ها</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {recentSubs.length === 0 ? (
              <EmptyHint text="هنوز اشتراکی صادر نشده است." />
            ) : (
              <ul className="max-h-80 divide-y overflow-y-auto scrollbar-thin">
                {recentSubs.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-3 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {s.user?.name ?? "—"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {s.plan?.name ?? "—"}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        پایان: {faDate(s.endDate)}
                      </div>
                    </div>
                    <Badge
                      className={
                        s.status === "ACTIVE"
                          ? "bg-primary/15 text-primary hover:bg-primary/20"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }
                    >
                      {s.status === "ACTIVE" ? (
                        <>
                          <CheckCircle2 className="size-3" />
                          فعال
                        </>
                      ) : (
                        "غیرفعال"
                      )}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent AI Orders */}
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bot className="size-4 text-primary" />
              آخرین سفارش‌های هوش مصنوعی
            </CardTitle>
            {counts.pendingAiOrders > 0 && (
              <Badge className="bg-amber-500 text-white hover:bg-amber-500/90">
                {faNum(counts.pendingAiOrders)} در انتظار
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {recentAiOrders.length === 0 ? (
            <EmptyHint text="هنوز سفارش هوش مصنوعی ثبت نشده است." />
          ) : (
            <ul className="max-h-80 divide-y overflow-y-auto scrollbar-thin">
              {recentAiOrders.map((o) => (
                <li
                  key={o.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span dir="ltr" className="text-sm font-medium">
                        {o.user?.phone ?? "—"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {o.productName ?? o.product?.name ?? "—"}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {faDateTime(o.createdAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-primary">
                      {faToman(o.amountToman)}
                    </span>
                    <AiOrderStatusBadge status={o.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Separator className="bg-border/60" />
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="py-10 text-center text-sm text-muted-foreground">{text}</div>
  );
}
